import React from "react";
import { widget } from "@charting_library";

import { useJsonModal } from "@src/hooks/useJsonModal";
import { useToastActions } from "@src/hooks/useToasts";

import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";

import { useRunResultContext } from "../RunResults/RunResultContext";
import * as tvUtil from "./utils";

export const TradingViewAdvanced: React.FC<T.ArrayTypedPropsForDatafeed> = (props) => {
  const tvWidgetRef = React.useRef<HTMLDivElement>(null);

  const {
    tradingViewWidgetRef,
    tradingViewContainerRef,
    datafeedRef,
    savedViewportRef,
    run,
    runType,
    setCandleSource,
  } = useRunResultContext();

  const { showJsonModal } = useJsonModal();
  const { showToast } = useToastActions();

  // Track resolution changes (isResolutionChanging available for future loading UI)
  const [_isResolutionChanging, setIsResolutionChanging] = React.useState(false);
  const currentResolutionRef = React.useRef<string>(props.api_period);
  const originalResolutionRef = React.useRef<string>(props.api_period);
  const viewportRestoreTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stops the custom study render; aborted before the chart's studies are removed and when the widget goes
  const studiesRenderRef = React.useRef<AbortController | null>(null);

  /**
   * Handle resolution change from TradingView header dropdown
   * Fetches new candle data from API and updates the datafeed
   */
  const handleResolutionChange = React.useCallback(
    async (
      chart: T.TV.IChartWidgetApi,
      datafeed: T.ExternalDatafeed,
      newInterval: T.TV.ResolutionString,
    ) => {
      // currentResolutionRef tracks the PREVIOUS resolution (set in onChartReady and after each change)
      // onIntervalChanged fires AFTER TradingView changed resolution, so chart.resolution() is already NEW
      const previousApiPeriod = currentResolutionRef.current;
      const newApiPeriod = tvUtil.tvResolutionToApiPeriod(newInterval);

      // Skip if same as our tracked resolution (TradingView may fire duplicate events)
      if (newApiPeriod === previousApiPeriod) {
        return;
      }

      // Clear any pending viewport restore from previous resolution change
      if (viewportRestoreTimeoutRef.current) {
        clearTimeout(viewportRestoreTimeoutRef.current);
        viewportRestoreTimeoutRef.current = null;
      }

      // CRITICAL: Set pending flag SYNCHRONOUSLY before any async operations
      // This makes getBars return noData when TradingView requests data during resolution change
      datafeed.setPendingResolutionChange(true);

      setIsResolutionChanging(true);

      try {
        // 1. Save current viewport center
        const visibleRange = chart.getVisibleRange();
        const centerTime = (visibleRange.from + visibleRange.to) / 2;
        const rangeWidth = visibleRange.to - visibleRange.from;

        // 2. Calculate time range to fetch (visible range + 2x buffer)
        const bufferMultiplier = 2;
        const from = centerTime - rangeWidth * bufferMultiplier;
        const to = centerTime + rangeWidth * bufferMultiplier;

        console.log("[handleResolutionChange] Fetching candles:", {
          period: newApiPeriod,
          from: new Date(from * 1000).toISOString(),
          to: new Date(to * 1000).toISOString(),
        });

        // 3. Fetch candles from API
        const candles = await tdApi.getAllCandles(
          props.symbol.exchange,
          props.symbol.name,
          "desc",
          new Date(from * 1000).toISOString(),
          new Date(to * 1000).toISOString(),
          newApiPeriod,
        );

        // 4. Convert to typed arrays and update datafeed
        const typedCandles = tvUtil.convertApiCandlesToTypedArray(candles);
        datafeed.updateCandleData(typedCandles);

        // 5. Handle indicators - hide if resolution difference from ORIGINAL >= 4x
        const originalApiPeriod = originalResolutionRef.current;
        const origMinutes = tvUtil.getResolutionMinutes(
          tvUtil.resolutionMap.find((r) => r.apiPeriod === originalApiPeriod)?.tvRes || "5", // 5m fallback
        );
        const newMinutes = tvUtil.getResolutionMinutes(newInterval);
        const ratio = Math.max(origMinutes, newMinutes) / Math.min(origMinutes, newMinutes);

        if (ratio >= 4) {
          // Stop the study render first, so a study it is still creating does not outlive the removal
          studiesRenderRef.current?.abort();
          chart.removeAllStudies();
        }

        // 6. Update current resolution tracking + enable incremental API fetching for scrolling
        currentResolutionRef.current = newApiPeriod;
        datafeed.state.isCustomResolution = newApiPeriod !== originalResolutionRef.current;
        setCandleSource({ currentResolution: newApiPeriod });

        // 7. Force TradingView to clear its internal cache and re-request data
        datafeed.state.onResetCacheNeeded?.();

        // 8. Force chart to reload data after cache is cleared
        chart.resetData();

        // 9. Restore viewport to same center timestamp
        // Use 250ms delay to allow TradingView to process data reset and reload
        viewportRestoreTimeoutRef.current = setTimeout(() => {
          viewportRestoreTimeoutRef.current = null;
          chart.setVisibleRange({
            from: centerTime - rangeWidth / 2,
            to: centerTime + rangeWidth / 2,
          });
        }, 250);
      } catch (error) {
        console.error("Resolution change failed:", error);
        showToast({ message: "Failed to change resolution", type: "warning" });
        // Clear pending flag on error so getBars works normally again
        datafeed.setPendingResolutionChange(false);
      } finally {
        setIsResolutionChanging(false);
      }
    },
    [props.symbol.exchange, props.symbol.name, showToast, setCandleSource],
  );

  // Cleanup viewport restore timeout on unmount
  React.useEffect(() => {
    return () => {
      if (viewportRestoreTimeoutRef.current) {
        clearTimeout(viewportRestoreTimeoutRef.current);
      }
    };
  }, []);

  // Setup global callback for telemetry modal (called from context menu)
  React.useEffect(() => {
    let isMounted = true;

    window.showTelemetryModal = async () => {
      const chart = tradingViewWidgetRef.current?.activeChart();
      if (!chart) {
        console.warn("Chart not ready for telemetry");
        return;
      }

      try {
        // Get crosshair position - this is the bar the user right-clicked on
        const crosshairData = tvUtil.getCurrentCrosshairData();

        if (!crosshairData?.time) {
          showJsonModal(
            {
              error: "No bar selected",
              hint: "Move your cursor over a candle and right-click to view telemetry for that bar",
            },
            "Telemetry Error",
          );
          return;
        }

        // Get execution_id from context (only available for executions, not backtests)
        const executionId = runType === "execution" ? run?.id : null;

        if (!executionId) {
          showJsonModal(
            {
              error: "Execution ID not available",
              hint:
                runType === "backtest"
                  ? "Telemetry is only available for live executions, not backtests"
                  : "Run information not found in context",
            },
            "Telemetry Error",
          );
          return;
        }

        const barTime = crosshairData.time;
        const barTimeMs = barTime * 1000;
        const barTimeISO = new Date(barTimeMs).toISOString();

        // Calculate buffer based on current chart resolution (±1 candle width)
        const currentInterval = chart.resolution();
        const bufferSeconds = tvUtil.getResolutionMinutes(currentInterval) * 60;
        const candleTimeRange = {
          from: barTime - bufferSeconds,
          to: barTime + bufferSeconds,
        };

        // Fetch events for the execution (high limit to avoid missing older events on long runs)
        const [allEventsResponse, telemetryResponse] = await Promise.all([
          tdApi.getEventsForTimeRange(executionId, undefined, 1000),
          tdApi.getTelemetryForTimeRange(executionId, candleTimeRange),
        ]);

        // Guard against unmount during fetch
        if (!isMounted) return;

        // Filter events by candle_time matching the clicked bar (±1 candle buffer)
        // candle_time is stored in event_data and represents when the signal candle closed
        const bufferMs = bufferSeconds * 1000;
        const allEvents = allEventsResponse?.items || [];
        const barEvents = allEvents.filter((e) => {
          const candleTime = e.event_data?.candle_time;
          if (!candleTime) return false;
          const candleTimeMs = new Date(candleTime).getTime();
          if (Number.isNaN(candleTimeMs)) return false;
          return Math.abs(candleTimeMs - barTimeMs) <= bufferMs;
        });

        // Prepare telemetry data for display
        const telemetryData = {
          bar: {
            time_utc: barTimeISO,
            time_local: new Date(barTimeMs).toLocaleString(),
            price: crosshairData.price,
          },
          events: barEvents,
          candle_telemetry: telemetryResponse?.items || [],
          summary: {
            total_events: barEvents.length,
            total_candles: telemetryResponse?.items?.length || 0,
            error_count: barEvents.filter((e) => e.level === "ERROR").length,
            warning_count: barEvents.filter((e) => e.level === "WARNING").length,
          },
        };

        showJsonModal(telemetryData, `Telemetry @ ${new Date(barTimeMs).toLocaleString()}`);
      } catch (err) {
        if (!isMounted) return;
        showJsonModal(
          {
            error: err instanceof Error ? err.message : String(err),
            timestamp: new Date().toISOString(),
          },
          "Telemetry Error",
        );
      }
    };

    return () => {
      isMounted = false;
      delete window.showTelemetryModal;
    };
  }, [tradingViewWidgetRef, run, runType, showJsonModal]);

  // Chart initialization effect - runs once per mount (controlled by key prop from parent)
  React.useEffect(() => {
    if (!tvWidgetRef.current) return;

    // Set the container ref for scrolling purposes
    tradingViewContainerRef.current = tvWidgetRef.current;

    let unsubscribeCrosshair: (() => void) | null = null;
    let unsubscribeInterval: (() => void) | null = null;
    // The initial positioning runs after a delay; a newer cache can remove this widget before then
    let positionTimeout: ReturnType<typeof setTimeout> | undefined;

    // Read before this widget reports its own ranges into the same ref
    const viewportToRestore = savedViewportRef.current;

    // Marker ids on the shared positions belong to a removed widget after a remount; clear them so this
    // widget draws its own
    props.positions?.forEach((pos) => {
      pos.tv_shape_id = null;
      pos.tv_temp_shape_id = null;
    });

    try {
      const { widgetOptions, datafeed } = tvUtil.setWidgetOptions(
        { container: tvWidgetRef.current },
        props,
      );
      datafeedRef.current = datafeed; // Store datafeed reference
      tvUtil.setCurrentDatafeed(datafeed); // Make it available to utility functions

      // Clear any existing widget
      if (tradingViewWidgetRef.current) {
        tradingViewWidgetRef.current.remove();
        tradingViewWidgetRef.current = null;
      }

      const tvWidget = new widget(widgetOptions);
      tradingViewWidgetRef.current = tvWidget;

      tvWidget.onChartReady(() => {
        const chart: T.TV.IChartWidgetApi = tvWidget.activeChart();

        // Initialize resolution tracking from chart's actual resolution
        // This ensures our refs match TradingView's initial resolution (not props.api_period)
        const initialResolution = chart.resolution();
        const initialApiPeriod = tvUtil.tvResolutionToApiPeriod(initialResolution);
        currentResolutionRef.current = initialApiPeriod;
        originalResolutionRef.current = initialApiPeriod;

        // Report to context for UI indicators
        setCandleSource({
          originalResolution: initialApiPeriod,
          currentResolution: initialApiPeriod,
        });

        // Initialize crosshair tracking for telemetry
        unsubscribeCrosshair = tvUtil.initCrosshairTracking(chart);

        // Automatically render all custom indicators
        const studiesRender = new AbortController();
        studiesRenderRef.current = studiesRender;
        tvUtil.renderStudies(chart, studiesRender.signal);

        // Listen for range changes to render positions and boundary markers
        chart.onVisibleRangeChanged().subscribe(null, (timeRange: T.TV.VisibleTimeRange) => {
          savedViewportRef.current = {
            range: timeRange,
            latestTime: props.candles?.times?.[0],
            resolution: chart.resolution(),
          };
          tvUtil.drawPositionsOnTimeFrame(chart, timeRange);
          tvUtil.drawBoundaryMarkersOnTimeFrame(chart, timeRange);
        });

        // Listen for resolution changes from the header dropdown
        const intervalEvent = chart.onIntervalChanged();
        const onIntervalChanged = (newInterval: T.TV.ResolutionString) => {
          if (datafeedRef.current) {
            handleResolutionChange(chart, datafeedRef.current, newInterval);
          }
        };
        intervalEvent.subscribe(null, onIntervalChanged);
        unsubscribeInterval = () => intervalEvent.unsubscribe(null, onIntervalChanged);

        // Move the chart to show the relevant time range
        // Priority: 1) Candles (parquet data), 2) Positions (API fallback mode)
        positionTimeout = setTimeout(() => {
          const hasCandles = props.candles?.times?.length > 0;
          const hasPositions = props.positions?.length > 0;

          let range: T.TV.VisibleTimeRange | undefined;

          if (viewportToRestore) {
            // Rebuilt on a newer cache: stay where the previous widget was
            range = tvUtil.restoreVisibleRange(viewportToRestore, props.candles?.times?.[0]);
          } else if (hasCandles) {
            range = tvUtil.getInitialRangeFromCandles(props.candles);
          } else if (hasPositions) {
            // API fallback mode - use positions to determine initial range
            const resolution = chart.resolution() as T.TV.ResolutionString;
            range = tvUtil.getInitialRangeFromPositions(
              props.positions,
              resolution,
              props.openOnEntry,
            );
          }

          const rangeApplied = range ? chart.setVisibleRange(range) : Promise.resolve();

          // A resolution picked on the previous widget is applied once the range has, so the resolution
          // change fetches and keeps the restored window
          const savedResolution = viewportToRestore?.resolution;
          if (savedResolution && savedResolution !== chart.resolution()) {
            void rangeApplied.then(() => chart.setResolution(savedResolution));
          }
        }, 500);
      });
    } catch (error) {
      console.error("TradingView widget initialization error:", error);
    }

    return () => {
      clearTimeout(positionTimeout);
      // Stop a study render that is still running before the widget goes
      studiesRenderRef.current?.abort();

      // Unsubscribe from crosshair and interval change events
      unsubscribeCrosshair?.();
      unsubscribeInterval?.();
      tvUtil.resetCrosshairTracking();

      if (tradingViewWidgetRef.current) {
        try {
          tradingViewWidgetRef.current.remove();
          tradingViewWidgetRef.current = null;
        } catch (error) {
          console.error("Error removing TradingView widget:", error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - component unmounts/remounts via key prop

  return (
    <div
      ref={tvWidgetRef}
      className="TradingViewChart"
      style={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    />
  );
};
